import { useMemo, useState, type ReactNode } from 'react';
import { TreeBody, type Metrics } from './TreeBody';
import { computeLayout } from './layout';
import { Tooltip } from '../Tooltip';

/** Which way a generation grows away from its parent. */
export type TreeOrientation = 'horizontal' | 'vertical';

/**
 * A hierarchy drawn in **lanes**, either way up: left to right (first lane on the left, each
 * further lane a column to the right) or top to bottom (first lane on top, each further lane
 * a row down).
 *
 * The reference names no tree component, so this is a new shared one rather than a chart
 * built inside a feature folder — the next module with a hierarchy uses this, not a second
 * copy of it.
 *
 * **A record is drawn in its OWN lane. Always.** Not its parent’s, not one past its parent’s.
 * A record must never appear under a rank it does not have — that is the one question a chart
 * of ranks exists to answer, and no arrangement of the tree is worth answering it wrongly.
 * This is why the chart is laid out by `computeLayout` and painted from the result rather
 * than nested: a nested list can only put a child AFTER its parent.
 *
 * **The columns are the ladder, not what happens to be in it.** Pass `lanes` — every rank
 * there is, in order — and each gets a column whether or not a record sits there. An empty
 * column is the point: it is what a skipped rank looks like. A push past the last rank adds
 * an overflow column, which draws but carries no label and no actions.
 */
export function TreeChart<T>({
  nodes,
  getKey,
  getChildren,
  renderNode,
  lanes: ladder,
  laneOf,
  renderLaneActions,
  laneAction,
  collapsible = true,
  orientation = 'horizontal',
  label,
}: {
  nodes: readonly T[];
  getKey: (node: T) => string;
  getChildren: (node: T) => readonly T[];
  renderNode: (
    node: T,
    state: {
      expanded: boolean;
      hasChildren: boolean;
      /**
       * True when the node opens a **sub-tier** of its lane — its parent holds the same rank.
       * Render it smaller; `<TreeNodeCard sub>` is what that means for the standard card.
       */
      sub: boolean;
    },
  ) => ReactNode;
  /**
   * Every lane there is, in order — the ladder the chart is drawn against.
   *
   * **`key` identifies the lane, and it must be the identity of the thing the lane stands
   * for — not its ordering value.** Two rows can share an order (one restored onto a value a
   * renumber already handed out), and matching on that puts records from the second lane into
   * the first lane's column.
   */
  lanes?: readonly { key: string; label: string }[];
  /** The lane this node belongs to, as a `lanes` key. */
  laneOf?: (node: T) => string;
  /** A lane's own actions, revealed when its header is hovered or focused. */
  renderLaneActions?: (lane: {
    key: string;
    label: string;
    isLast: boolean;
    /** How many records are in this lane. Zero on an empty column. */
    count: number;
  }) => ReactNode;
  /** Rendered past the last lane, level with the chart — an "add another lane" affordance. */
  laneAction?: ReactNode;
  /** When false, every branch renders open and no disclosure button is drawn. */
  collapsible?: boolean;
  orientation?: TreeOrientation;
  /** Names the list for assistive technology. */
  label: string;
}) {
  // Collapsed rather than expanded, so a chart opens fully drawn — a chart that arrived
  // collapsed would hide the thing it exists to show.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const vertical = orientation === 'vertical';

  /** The ladder as keys, for turning a node's lane key into an index. */
  const laneKeys = useMemo(() => (ladder ?? []).map((lane) => lane.key), [ladder]);

  const metrics: Metrics = vertical
    ? {
        cardMain: LANE_H,
        cardCross: CARD_W,
        gutter: GUTTER,
        crossGap: COL_GAP,
        subIndent: SUB_INDENT,
        neck: NECK,
        circle: CIRCLE,
        stem: STEM,
      }
    : {
        cardMain: CARD_W,
        cardCross: ROW_H,
        gutter: GUTTER,
        crossGap: ROW_GAP,
        subIndent: SUB_INDENT,
        neck: NECK,
        circle: CIRCLE,
        stem: STEM,
      };

  const layout = useMemo(
    () =>
      computeLayout(nodes, {
        getKey,
        getChildren,
        /*
          Resolved here rather than in a memo of its own: a memo whose value is a function is
          one the compiler cannot prove stable, and hoisting it only moved the problem.
        */
        laneOf: (node: T) => {
          if (!laneOf || laneKeys.length === 0) return 0;
          const found = laneKeys.indexOf(laneOf(node));
          return found === -1 ? 0 : found;
        },
        laneCount: Math.max(laneKeys.length, 1),
        cardMain: metrics.cardMain,
        gutter: metrics.gutter,
        subIndent: metrics.subIndent,
        collapsed,
        collapsible,
      }),
    [
      nodes,
      getKey,
      getChildren,
      laneOf,
      laneKeys,
      metrics.cardMain,
      metrics.gutter,
      metrics.subIndent,
      collapsed,
      collapsible,
    ],
  );

  /*
    The columns to draw: one per rank, plus one for every push that ran past the last rank.

    An overflow column carries **no label and no actions**. Naming it would put one rank at
    the head of two columns, and offering Edit there would act on the single rank both of
    them named — which reads as "editing one tier edited them all". It still draws, because
    the records in it are real.
  */
  const columns = useMemo(() => {
    const ranks = ladder ?? [];

    if (layout.lanes <= ranks.length) {
      return ranks;
    }

    return [
      ...ranks,
      ...Array.from({ length: layout.lanes - ranks.length }, (_, index) => ({
        key: `__overflow-${index}`,
        label: '',
      })),
    ];
  }, [ladder, layout.lanes]);

  /*
    How many records each column holds. Placement and rank are now the same thing, so this
    counts both at once — it did not used to, and the difference was the push.
  */
  const counts = useMemo(() => {
    const tally = new Array<number>(Math.max(layout.lanes, 1)).fill(0);

    for (const entry of layout.placed) {
      if (entry.lane >= 0 && entry.lane < tally.length) tally[entry.lane] += 1;
    }

    return tally;
  }, [layout]);

  const offsets = layout.laneOffsets;
  const spans = layout.laneSpans;

  return (
    /*
      The chart's extent is on the axis it grows along; the other axis fills the panel, or the
      lane rules stop where the cards stop and the chart cannot centre in space it does not
      know it has.
    */
    <div
      className={`relative flex ${
        vertical ? 'min-h-max min-w-full flex-row' : 'min-h-full min-w-max flex-col'
      }`}
    >
      {ladder ? (
        /*
          Sticky on the axis the header does NOT run along: a column header stays put while
          the reader scrolls down, a row rail while they scroll right. It carries the panel's
          own background, or the cards show through it.
        */
        <div className={`sticky z-20 shrink-0 bg-white ${vertical ? 'left-0' : 'top-0'}`}>
          <LaneHeaders
            lanes={columns}
            counts={counts}
            spans={spans}
            vertical={vertical}
            renderActions={renderLaneActions}
          />
        </div>
      ) : null}

      <div className={`relative flex flex-1 ${vertical ? 'flex-col' : ''}`}>
        {/*
          The lane rules, behind the records and spanning the whole panel — a lane reads as a
          column with two sides rather than as a gap between cards. Solid grey where the
          connectors are dashed: a rule is chrome marking the lane, a connector is a
          relationship between two records, and they must not read as the same kind of line.
        */}
        {columns.map((lane, index) => {
          const at = (offsets[index] ?? 0) + (spans[index] ?? 0) + GUTTER / 2;

          return (
            <span
              key={`rule-${lane.key}`}
              aria-hidden="true"
              className="absolute bg-slate-200"
              style={
                vertical
                  ? { left: 0, right: 0, height: 1, top: at }
                  : { top: 0, bottom: 0, width: 1, left: at }
              }
            />
          );
        })}

        {/*
          Centred on the cross axis, through an auto margin rather than `justify-content` —
          which clips the leading edge once the chart outgrows the panel.
        */}
        <div className={`relative ${vertical ? 'mx-auto' : 'my-auto'}`}>
          <TreeBody
            layout={layout}
            renderNode={renderNode}
            vertical={vertical}
            collapsible={collapsible}
            onToggle={toggle}
            label={label}
            metrics={metrics}
          />
        </div>

        {/*
          Past the last lane and level with the chart: it adds a lane rather than describing
          one, so it reads as the next thing along the row of columns.
        */}
        {laneAction ? (
          <div
            className={`relative flex shrink-0 items-center ${vertical ? 'mx-auto' : 'my-auto'}`}
            style={vertical ? { paddingTop: GUTTER } : { paddingLeft: GUTTER }}
          >
            {laneAction}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A card's width. Fixed, so every lane lines up across every branch. */
const CARD_W = 232;
/** A card's height in a left-to-right chart, where siblings spread downwards. */
const ROW_H = 44;
/** A card's height in a top-to-bottom chart, where the lanes stack downwards. */
const LANE_H = 44;
/** The short run between a node and its own disclosure. */
const NECK = 8;
/** The disclosure’s own diameter. */
const CIRCLE = 20;
/** The run out of a disclosure before a connector turns. */
const STEM = 24;

/**
 * Between one lane and the next — it holds the connector run, and it is the gap the
 * disclosure hangs into.
 *
 * **It can never be tighter than the disclosure needs**, in either orientation: the circle
 * sits NECK past a card’s trailing edge and is CIRCLE across, so anything less than
 * NECK + CIRCLE puts it on top of the next card. That is exactly what the top-to-bottom
 * chart did while a same-rank child was drawn 20px under its parent against a 28px
 * disclosure — the circle straddled both cards. A same-rank child takes its own cross slot
 * now, so nothing is stacked inside a lane, and this floor is here so no future tweak can
 * bring the overlap back.
 *
 * STEM on top of it, because a connector needs room to run straight before it turns.
 */
const GUTTER = Math.max(76, NECK + CIRCLE + STEM);
/**
 * How far a sub-tier is inset inside its lane.
 *
 * A child at its parent’s own rank belongs in the same column, and drawn level with the
 * parent it hangs from it reads as a peer. The inset gives it a channel of its own inside the
 * tier — which is where its connector runs, so the width is load-bearing rather than
 * decorative.
 *
 * The LANE grows by it; the card does not shrink. Taking it out of the card aligns every
 * trailing edge and looks tidier — but a card is only `cardMain` long, and top to bottom that
 * is one row’s height, so two sub-tiers deep there would be no card left at all. A tier with
 * no same-rank children is exactly as wide as it ever was.
 */
const SUB_INDENT = 22;
/** Between one sibling and the next, going down a left-to-right chart. */
const ROW_GAP = 20;
/** Between one sibling and the next, going across a top-to-bottom chart. */
const COL_GAP = 28;

/** The width of the row rail that labels the lanes of a top-to-bottom chart. */
const RAIL = 150;

/**
 * The lane names, as a table header over the columns.
 *
 * **Each cell is a whole lane, not a label floating over one.** A cell spans the lane's own
 * thickness plus its gutter and closes on the same boundary the lane rule is drawn at, so the
 * header dividers and the chart dividers are the same lines. The measurements come from the
 * body, so the header cannot drift from the columns it frames.
 *
 * A cell with no label is an OVERFLOW column — a push carried records past the last rank —
 * and it gets no actions either.
 */
function LaneHeaders({
  lanes,
  counts,
  spans,
  vertical,
  renderActions,
}: {
  lanes: readonly { key: string; label: string }[];
  counts: number[];
  spans: number[];
  vertical: boolean;
  renderActions?: (lane: {
    key: string;
    label: string;
    isLast: boolean;
    count: number;
  }) => ReactNode;
}) {
  return (
    <div className={`flex shrink-0 ${vertical ? 'flex-col' : 'flex-row'} items-stretch`}>
      {lanes.map((lane, index) => {
        const span = spans[index] ?? 0;
        /*
          Every band is its lane plus a whole gutter, and every label is inset by half of one.
          The first cell used to be the exception — short by half a gutter, with no inset —
          because lane 0 began at zero. It no longer does: the layout gives it the same half
          gutter of clearance every other lane has, so the special case is gone with it.
        */
        const size = span + GUTTER;
        const inset = GUTTER / 2;

        return (
          <div
            key={`head-${lane.key}`}
            className={[
              'group flex shrink-0 overflow-hidden',
              // The table-header ground, and the rule the lane's own divider continues from.
              'border-b border-r border-slate-200 bg-slate-50',
              vertical ? 'items-start' : 'items-center',
            ].join(' ')}
            style={
              vertical
                ? { height: size, width: RAIL }
                : { width: size, paddingLeft: inset + 12, paddingRight: GUTTER / 2 }
            }
          >
            {/*
              The label's own band: in a rail it is the lane's thickness, pushed down by the
              cell's inset so it sits across from the records it names.
            */}
            <div
              className={`flex w-full min-w-0 items-center gap-1 ${vertical ? 'px-3' : ''}`}
              style={vertical ? { marginTop: inset, height: Math.max(span, 24) } : undefined}
            >
              {/* Muted: the header names the lane, and the records in it are the content. */}
              <Tooltip label={lane.label} block>
              <span className="min-w-0 truncate text-xs text-slate-400">
                {lane.label}
              </span>
              </Tooltip>

              {/*
                Pushed to the cell's trailing edge, so a row of kebabs reads down one edge
                instead of tracking each label's length.
              */}
              {renderActions && lane.label ? (
                <span className="ml-auto shrink-0 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
                  {renderActions({
                    key: lane.key,
                    label: lane.label,
                    isLast: index === lanes.length - 1,
                    count: counts[index] ?? 0,
                  })}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
